import React, { createContext, useContext, useState, useEffect } from 'react';
import Client from '../base/client';
import BinarySocket from '../../_common/base/socket_base';
import { formatMoney } from '../../_common/base/currency_base';
import { getAccountType } from '../../config';
import { localize } from '@deriv-com/translations';
import Language from '../../_common/language';
import DFundedCore from '../../_common/base/dfunded/core';

// Create the context
const AppContext = createContext(null);

/**
 * AppProvider - Manages application-wide state
 *
 * Provides:
 * - Authentication state (isLoggedIn)
 * - Account information (currency, balance, loginid, accountType)
 * - Language configuration (currentLanguage, availableLanguages)
 * - Mobile menu state (isMobileMenuOpen)
 * - Loading states
 * - Helper functions (getFormattedBalance, handleLanguageChange, etc.)
 */
const AppProvider = ({ children }) => {
    const Accounts = require('../../_common/base/accounts');
    const [isLoggedIn, setIsLoggedIn] = useState(Client.isLoggedIn());
    const [isLoading, setIsLoading] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [accountInfo, setAccountInfo] = useState({
        currency   : '',
        balance    : undefined,
        loginid    : '',
        accountType: '',
    });
    const [allAccounts, setAllAccounts] = useState(() => Accounts.getClientAccounts());
    
    // Store balances for all accounts (like multi_platform_sites)
    const [allAccountsBalance, setAllAccountsBalance] = useState({});

    // Centralized language configuration - used by both desktop and mobile
    const [currentLanguage] = useState(() => Language.get());
    const [availableLanguages] = useState(() => {
        const allLanguages = Language.getAll();
        const allowedLanguages = Language.getAllowedLanguages();

        // Map language codes to display format with flags
        return allowedLanguages.map((code) => ({
            code,
            name: allLanguages[code],
            flag: code.toLowerCase(),
        }));
    });

    // A stale legacy session may be cleared by config.js getSocketURL AFTER this
    // provider captured Client.isLoggedIn() at mount. Listen for the clear event
    // so the header drops to a consistent logged-out state (no zombie header).
    useEffect(() => {
        const handleSessionCleared = () => {
            setIsLoggedIn(false);
            setIsLoading(false);
            setAccountInfo({
                currency   : '',
                balance    : undefined,
                loginid    : '',
                accountType: '',
            });
            setAllAccounts({});
            setAllAccountsBalance({});
        };
        window.addEventListener('smarttrader:session-cleared', handleSessionCleared);
        return () => window.removeEventListener('smarttrader:session-cleared', handleSessionCleared);
    }, []);

    // Subscribe to balance updates (balance is the auth confirmation in new API)
    useEffect(() => {
        const handleBalance = (response) => {
            if (!response || response.msg_type !== 'balance' || !response.balance) {
                return;
            }

            const balance = response.balance;
            
            // Handle all accounts balance (OAuth login returns balance.accounts)
            if (balance.accounts) {
                setAllAccountsBalance(balance.accounts);
                
                // Also update current account info from the accounts object
                const activeLoginId = Accounts.getLoginId();
                const currentAccountBalance = balance.accounts[activeLoginId];
                if (currentAccountBalance) {
                    setAccountInfo({
                        currency   : currentAccountBalance.currency || '',
                        balance    : currentAccountBalance.balance || 0,
                        loginid    : activeLoginId || '',
                        accountType: getAccountType() || '',
                    });
                }
            } else if (balance.loginid) {
                // Single-account balance subscription update.
                setAllAccountsBalance(prev => ({
                    ...prev,
                    [balance.loginid]: {
                        balance: balance.balance,
                        currency: balance.currency,
                    }
                }));
                
                // Only update accountInfo if this is the active account
                const activeLoginId = Accounts.getLoginId();
                if (balance.loginid === activeLoginId) {
                    setAccountInfo({
                        currency   : balance.currency || '',
                        balance    : balance.balance || 0,
                        loginid    : balance.loginid || '',
                        accountType: getAccountType() || '',
                    });
                }
            }
            
            setIsLoggedIn(true);
            setIsLoading(false);
            
            // Refresh account list in case of updates
            setAllAccounts(Accounts.getClientAccounts());

            // Clear token exchange flag if it exists
            if (window.tokenExchangeInProgress) {
                window.tokenExchangeInProgress = false;
            }
        };

        // Listen for balance responses (auth confirmation)
        // Use a one-time wait that gets set up again after processing
        let timeoutId;
        const waitForBalance = () => {
            BinarySocket.wait('balance').then((response) => {
                handleBalance(response);
                // Schedule next wait (avoid recursive stack growth)
                timeoutId = setTimeout(waitForBalance, 0);
            }).catch(() => {
                // On error, retry after delay
                timeoutId = setTimeout(waitForBalance, 1000);
            });
        };
        
        waitForBalance();
        
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, []);

    // Helper function to format balance
    const getFormattedBalance = (balance, currency) =>
        formatMoney(currency || accountInfo.currency || 'USD', balance || accountInfo.balance || 0, true);

    // Helper function to get account type display name
    const getAccountTypeDisplay = (loginid) => {
        const id = loginid || accountInfo.loginid;
        if (!isLoggedIn && !id) return '';
        // A DFunded session shows which kind of funded account is trading
        // ("DFunded · Phase 1"), not Real/Demo.
        if (Client.isDFundedSession && Client.isDFundedSession()) {
            const kind = DFundedCore.getDFundedAccountType();
            return kind ? `DFunded · ${kind}` : 'DFunded';
        }
        // is_virtual/account_type is authoritative (v2 demo accounts use a DOT
        // prefix the legacy VR check misses); VR prefix is only the fallback.
        const isDemo = Accounts.isDemoLoginId(id, allAccounts[id]);
        return isDemo ? localize('Demo') : localize('Real');
    };

    // Toggle mobile menu
    const toggleMobileMenu = () => {
        setIsMobileMenuOpen((prev) => !prev);
    };

    // Close mobile menu
    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    // Handle language change
    const handleLanguageChange = async (langCode) => {
        try {
            await Language.changeSelectedLanguage(langCode);
            return true;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to change language:', error);
            return false;
        }
    };

    const switchAccount = (loginid) => {
        Client.switchAccount(loginid);
    };

    // Helper function to get balance for a specific account
    const getAccountBalance = (loginid) => {
        const accountBalance = allAccountsBalance[loginid];
        if (accountBalance) {
            return {
                balance: accountBalance.balance || 0,
                currency: accountBalance.currency || accountInfo.currency || 'USD',
            };
        }
        // Fallback to account info from allAccounts
        const account = allAccounts[loginid];
        return {
            balance: account?.balance || 0,
            currency: account?.currency || accountInfo.currency || 'USD',
        };
    };

    const value = {
        isLoggedIn,
        isLoading,
        accountInfo,
        allAccounts,
        allAccountsBalance,
        isMobileMenuOpen,
        currentLanguage,
        availableLanguages,
        getFormattedBalance,
        getAccountTypeDisplay,
        getAccountBalance,
        setIsLoading,
        toggleMobileMenu,
        closeMobileMenu,
        handleLanguageChange,
        switchAccount,
        conversion: {
            getVRTCLoginId: Accounts.getVRTCLoginId,
            getRealLoginId: Accounts.getRealLoginId,
            getBalanceOffset: Accounts.getBalanceOffset,
            doesClientExist: Accounts.doesClientExist,
            isOnConversionAccount: Accounts.isOnConversionAccount,
        },
    };

    return React.createElement(AppContext.Provider, { value }, children);
};

/**
 * useApp - Custom hook to access app context
 *
 * @returns {Object} App context value
 * @throws {Error} If used outside AppProvider
 */
const useApp = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp must be used within an AppProvider');
    }
    return context;
};

export { AppProvider, useApp };
